import React, { useState, useEffect, useMemo } from 'react';
import { mediaUrl } from '../../utils/mediaUrl';
import api from '../../services/api';
import { AdminSidebar } from './AdminDashboardPage';
import AdminHeader from './AdminHeader';
import '../../css/adminSlider.css';
import { Plus, Pencil, Trash2, Loader2, AlertCircle, GalleryHorizontal, X } from 'lucide-react';
import { BannerThumbnail } from '../../components/admin/BannerThumbnail';
// Table.jsx lives at src/components/Table.jsx
import { Table } from '../../components/Table';
import { useToast } from '../../contexts/ToastContext';

const AdminSliderPage = () => {
  const { confirm } = useToast();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: 'Shop now',
    link: '',
    image: '',
    mobileImage: '',
    order: 0,
    isActive: true
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/banner-sliders');
      setBanners(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load banners');
      console.error(err);
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await api.put(`/banner-sliders/${editingBanner._id}`, formData);
      } else {
        await api.post('/banner-sliders', formData);
      }
      await loadBanners();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to save banner');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this banner?',
      message: 'This slider banner will be removed.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;
    try {
      await api.delete(`/banner-sliders/${id}`);
      await loadBanners();
    } catch (err) {
      setError('Failed to delete banner');
      console.error(err);
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await api.patch(`/banner-sliders/${id}`, { isActive: !currentStatus });
      await loadBanners();
    } catch (err) {
      setError('Failed to update banner status');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
    title: '',
    subtitle: '',
    buttonText: 'Shop now',
    link: '',
    image: '',
    mobileImage: '',
    order: 0,
    isActive: true
    });
    setEditingBanner(null);
  };

  const openModal = (banner = null) => {
    if (banner) {
      setFormData({
        title: banner.title || '',
        subtitle: banner.subtitle || '',
        buttonText: banner.buttonText || 'Shop now',
        link: banner.link || '',
        image: banner.image,
        mobileImage: banner.mobileImage || '',
        order: banner.order,
        isActive: banner.isActive
      });
      setEditingBanner(banner);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Order',
        accessor: 'order',
        Cell: ({ value }) => <span className="admin-order-badge">#{value ?? 0}</span>
      },
      {
        Header: 'Title',
        id: 'title',
        accessor: (b) => b.title || '-',
        Cell: ({ row }) => (
          <div>
            <div className="asl-cell-strong">{row.original.title || 'Untitled'}</div>
          </div>
        )
      },
      {
        Header: 'Subtitle',
        id: 'subtitle',
        accessor: (b) => b.subtitle || '—',
        Cell: ({ row }) => (
          <span className={`asl-cell-muted${row.original.subtitle ? '' : ' is-empty'}`}>
            {row.original.subtitle || '—'}
          </span>
        )
      },
      {
        Header: 'Image',
        id: 'image',
        disableSortBy: true,
        Cell: ({ row }) => (
          <BannerThumbnail
            src={mediaUrl(row.original.image)}
            alt={row.original.title || 'Slide'}
            title={row.original.title}
          />
        )
      },
      {
        Header: 'Status',
        id: 'status',
        Cell: ({ row }) => {
          const banner = row.original;
          return (
            <button
              className={`admin-status-pill ${banner.isActive ? 'active' : 'inactive'}`}
              onClick={() => toggleStatus(banner._id, banner.isActive)}
              title="Click to toggle status"
            >
              <span className="status-dot" />
              {banner.isActive ? 'Active' : 'Inactive'}
            </button>
          );
        }
      },
      {
        Header: 'Actions',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="admin-action-group">
            <button
              className="admin-btn-edit"
              onClick={() => openModal(row.original)}
              title="Edit Slide"
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              className="admin-btn-delete"
              onClick={() => handleDelete(row.original._id)}
              title="Delete Slide"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )
      }
    ],
    []
  );

  const pageHeading = (withAction) => (
    <div className="asl-heading">
      <div>
        <h1 className="asl-title">Slider Banners</h1>
        <p className="asl-subtitle">Slides in the homepage hero carousel.</p>
      </div>
      {withAction && (
        <div className="asl-heading-actions">
          <button type="button" className="asl-btn-primary" onClick={() => openModal()}>
            <Plus size={16} /> Add New Banner
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content">
          <AdminHeader title="Slider Banners" />
          <div className="asl-page">
            {pageHeading(false)}
            <div className="asl-state">
              <Loader2 size={26} className="asl-spinner" />
              <p className="asl-state-text">Loading slider banners...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Slider Banners" />
        <div className="asl-page">
          {pageHeading(true)}

          {error && (
            <div className="asl-error-message" role="alert">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {banners.length === 0 ? (
            <div className="asl-state asl-state--empty">
              <span className="asl-state-icon"><GalleryHorizontal size={20} /></span>
              <p className="asl-state-title">No slider banners configured yet</p>
              <p className="asl-state-text">Add homepage hero carousel slides to showcase featured textile collections.</p>
              <button type="button" className="asl-btn-primary" onClick={() => openModal()}>
                <Plus size={16} /> Add First Slide
              </button>
            </div>
          ) : (
            <Table columns={columns} data={banners} show />
          )}
        </div>

        {showModal && (
          <div className="asl-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="asl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="asl-modal-head">
                <h2>{editingBanner ? 'Edit slide' : 'Add new slide'}</h2>
                <button type="button" className="asl-modal-close" onClick={() => setShowModal(false)} aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="asl-form">
                <div className="asl-modal-body">
                  <div className="asl-form-group">
                    <label>Headline shown on the slide (e.g. "Kanjivaram Silk Sarees")</label>
                    <input
                      type="text"
                      maxLength={60}
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Short line under the headline (optional)</label>
                    <input
                      type="text"
                      maxLength={120}
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      placeholder="e.g. Handwoven in Kanchipuram. New colours this week."
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Button text (2–3 words)</label>
                    <input
                      type="text"
                      maxLength={24}
                      value={formData.buttonText}
                      onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                      placeholder="Shop now"
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Desktop image URL (wide, 1920 × 720)</label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      required
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Phone image URL (optional, portrait 1080 × 1350)</label>
                    <input
                      type="url"
                      value={formData.mobileImage}
                      onChange={(e) => setFormData({ ...formData, mobileImage: e.target.value })}
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Order in list</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      min="0"
                    />
                  </div>
                  <div className="asl-form-group">
                    <label>Navigation Link</label>
                    <input
                      type="text"
                      value={formData.link}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          link: e.target.value,
                        })
                      }
                      placeholder="/all-product?subCategory=Saree"
                    />
                  </div>
                  <div className="asl-checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>
                </div>
                <div className="asl-modal-actions">
                  <button type="button" className="asl-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="asl-btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSliderPage;
